import {
  Condition,
  ConditionGroup,
  TradeRule,
  TradeAction,
  SerializedGroup,
  SerializedCondition,
} from '@/modules/trade-rule-builder/model/trade-rule-builder';

// Type for deserialization input
type SerializedTradeRule = {
  ts: number;
  acts: { act: string; vol: number; sym: string }[];
  g: SerializedGroup;
  eID?: string;
  eName?: string;
  eCC?: string;
};

export function serializeRules(rules: TradeRule[]) {
  return rules.map(r => ({
    ts: Math.floor(new Date(r.timestamp).getTime() / 1000),
    acts: r.actions.map(a => ({ act: a.action, vol: a.volume, sym: a.symbol })),
    g: serializeGroup(r.rootGroup),
    eID: r.eventId,
    eName: r.eventName,
    eCC: r.eventCountry,
  }));
}
function serializeGroup(group: ConditionGroup): SerializedGroup {
  return {
    op: group.operator,
    conds: group.children.map(child =>
      'operator' in child
        ? serializeGroup(child)
        : { sid: child.seriesId, cmp: child.comparator, val: child.value },
    ),
  };
}

export function deserializeRules(data: SerializedTradeRule[]): TradeRule[] {
  return (data as SerializedTradeRule[]).map((r, i) => ({
    id: `${Date.now()}-${i}`,
    timestamp: r.ts
      ? new Date(r.ts * 1000).toISOString().slice(0, 19)
      : new Date().toISOString().slice(0, 19),
    actions: (r.acts as Array<{ act: TradeAction['action']; vol: number; sym: string }>).map(
      (a, j) => ({
        id: `${Date.now()}-${i}-${j}`,
        action: a.act,
        volume: a.vol,
        symbol: a.sym,
      }),
    ),
    rootGroup: deserializeGroup(r.g),
    eventId: r.eID || '',
    eventName: r.eName || '',
    eventCountry: r.eCC || '',
  }));
}

function deserializeGroup(g: SerializedGroup): ConditionGroup {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}-grp`,
    operator: g.op,
    children: g.conds.map((c: SerializedGroup | SerializedCondition) =>
      'conds' in c
        ? deserializeGroup(c as SerializedGroup)
        : {
            id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
            seriesId: (c as SerializedCondition).sid,
            field: 'value',
            comparator: (c as SerializedCondition).cmp,
            value: Number((c as SerializedCondition).val),
          },
    ),
  };
}

export function updateCondition<Val extends Condition[keyof Condition]>(
  ruleId: string,
  groupId: string,
  conditionId: string,
  key: keyof Condition,
  value: Val,
  setRules: React.Dispatch<React.SetStateAction<TradeRule[]>>,
) {
  setRules(prev =>
    prev.map(rule =>
      rule.id === ruleId
        ? {
            ...rule,
            rootGroup: updateConditionInGroup(rule.rootGroup, groupId, conditionId, key, value),
          }
        : rule,
    ),
  );
}

function updateConditionInGroup<Val extends Condition[keyof Condition]>(
  group: ConditionGroup,
  groupId: string,
  conditionId: string,
  key: keyof Condition,
  value: Val,
): ConditionGroup {
  if (group.id === groupId) {
    return {
      ...group,
      children: group.children.map(child =>
        'operator' in child ? child : child.id === conditionId ? { ...child, [key]: value } : child,
      ),
    };
  }
  return {
    ...group,
    children: group.children.map(child =>
      'operator' in child ? updateConditionInGroup(child, groupId, conditionId, key, value) : child,
    ),
  };
}

export function updateRuleField<Val extends TradeRule[keyof TradeRule]>(
  ruleId: string,
  key: keyof TradeRule,
  value: Val,
  setRules: React.Dispatch<React.SetStateAction<TradeRule[]>>,
) {
  setRules(prev => prev.map(rule => (rule.id === ruleId ? { ...rule, [key]: value } : rule)));
}

export function containsGroup(group: ConditionGroup, groupId: string): boolean {
  return group.children.some(
    child => 'operator' in child && (child.id === groupId || containsGroup(child, groupId)),
  );
}

export function updateGroupOperator(
  ruleId: string,
  groupId: string,
  value: 'AND' | 'OR',
  setRules: React.Dispatch<React.SetStateAction<TradeRule[]>>,
) {
  setRules(prev =>
    prev.map(rule =>
      rule.id === ruleId
        ? {
            ...rule,
            rootGroup: updateGroupOperatorInGroup(rule.rootGroup, groupId, value),
          }
        : rule,
    ),
  );
}

function updateGroupOperatorInGroup(
  group: ConditionGroup,
  groupId: string,
  value: 'AND' | 'OR',
): ConditionGroup {
  if (group.id === groupId) return { ...group, operator: value };
  return {
    ...group,
    children: group.children.map(child =>
      'operator' in child ? updateGroupOperatorInGroup(child, groupId, value) : child,
    ),
  };
}

export function addCondition(
  ruleId: string,
  groupId: string,
  setRules: React.Dispatch<React.SetStateAction<TradeRule[]>>,
) {
  setRules(prev =>
    prev.map(rule =>
      rule.id === ruleId
        ? { ...rule, rootGroup: addConditionToGroup(rule.rootGroup, groupId) }
        : rule,
    ),
  );
}

function addConditionToGroup(group: ConditionGroup, groupId: string): ConditionGroup {
  if (group.id === groupId) {
    return {
      ...group,
      children: [
        ...group.children,
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          seriesId: '',
          field: 'value',
          comparator: '>',
          value: 0,
        },
      ],
    };
  }
  return {
    ...group,
    children: group.children.map(child =>
      'operator' in child ? addConditionToGroup(child, groupId) : child,
    ),
  };
}

export function addGroup(
  ruleId: string,
  groupId: string,
  setRules: React.Dispatch<React.SetStateAction<TradeRule[]>>,
) {
  setRules(prev =>
    prev.map(rule =>
      rule.id === ruleId ? { ...rule, rootGroup: addGroupToGroup(rule.rootGroup, groupId) } : rule,
    ),
  );
}

function addGroupToGroup(group: ConditionGroup, groupId: string): ConditionGroup {
  if (group.id === groupId) {
    return {
      ...group,
      children: [
        ...group.children,
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}-grp`,
          operator: 'AND',
          children: [],
        },
      ],
    };
  }
  return {
    ...group,
    children: group.children.map(child =>
      'operator' in child ? addGroupToGroup(child, groupId) : child,
    ),
  };
}

export function deleteConditionFromGroup(
  group: ConditionGroup,
  groupId: string,
  conditionId: string,
): ConditionGroup {
  if (group.id === groupId) {
    return {
      ...group,
      children: group.children.filter(
        child => (!('operator' in child) && child.id !== conditionId) || 'operator' in child,
      ),
    };
  }
  return {
    ...group,
    children: group.children.map(child =>
      'operator' in child ? deleteConditionFromGroup(child, groupId, conditionId) : child,
    ),
  };
}

export function deleteGroupFromGroup(group: ConditionGroup, groupId: string): ConditionGroup {
  return {
    ...group,
    children: group.children
      .filter(child => !('operator' in child && child.id === groupId))
      .map(child => ('operator' in child ? deleteGroupFromGroup(child, groupId) : child)),
  };
}
